import { ChevronRight, Save, LoaderCircle, UserPen } from "lucide-react";
import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import TravelFormInput from "src/components/travel/form/TravelFormInput";
import TravelFormSelect from "src/components/travel/form/TravelFormSelect";
import { classNames } from "src/helpers/classNames";
import { formatDateToAPI, getEmergencyFields, getPersonalFields, INITIAL_STATE, parseDateFromAPI } from "src/helpers/travelHelper";
import TravelFromSkeleton from "./TravelFromSkeleton";
import TravelPassportDocuments from "./TravelPassportDocuments";
import { toast } from "react-toastify";
import TravelPreferences from "./TravelPreferences";
import TravelPayments from "./TravelPayments";
import { WorkspaceContext } from "src/context/workspace";
import { AuthContext } from "src/context/auth";
import TravelCustomFields from "./TravelCustomFields";

const FORM_SECTIONS = [
  { id: "general", title: "Traveller Details" },
  { id: "emergency", title: "Emergency Contact" },
  { id: "passport", title: "Travel Documents" },
  { id: "frequentFlyer", title: "Frequent Flyer Details" },
  { id: "customFields", title: "Company Reference Fields" },
  // { id: "payments", title: "Payments" },
];

const TravellerProfileForm = ({ page: propPage, group: propGroup, onBack }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search || "");
  const page = propPage || searchParams.get("page") || "";
  const group = propGroup || searchParams.get("group") || searchParams.get("travel-group") || "";
  const queryParams = `?page=${page}${group ? `&group=${group}` : ""}`;

  const [formData, setFormData] = useState(INITIAL_STATE);
  const [companies, setCompanies] = useState([]);
  // const [companySetup, setCompanySetup] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("general");
  const [preFerencesData, setPreFerencesData] = useState({});
  const [errors, setErrors] = useState({});
  const { details: workspaceDetails } = useContext(WorkspaceContext);
  const { user } = useContext(AuthContext);
  const savedDataRef = useRef(INITIAL_STATE);

  useEffect(() => {
    if (user?.email && !formData.username) {
      setFormData((prev) => ({
        ...prev,
        username: user.email,
      }));
    }
  }, [user?.email, formData.username]);

  useEffect(() => {
    const fetchCompanySetup = async () => {
      if (workspaceDetails?._id && formData?.companyUuid) {
        try {
          const res = await apiRequest("get", `/portal/workspaces/:workspace_id/travel/configuration/setup/company/:company_id${queryParams}`, {
            params: {
              workspace_id: workspaceDetails?._id,
              company_id: formData.companyUuid,
            },
          }).catch(() => null);
          if (res?.data?.data) {
            // setCompanySetup(res.data.data);
          }
        } catch (error) {
          console.error("Failed to load company setup configuration:", error);
        }
      }
    };

    fetchCompanySetup();
  }, [workspaceDetails?._id, formData?.companyUuid, queryParams]);

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [companiesRes, travellerRes, preferenRes] = await Promise.all([
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/companies${queryParams}`).catch(() => null),
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/travellers${queryParams}`).catch(() => null),
        apiRequest("get", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/system-reference-data${queryParams}`).catch(() => null),
      ]);

      if (companiesRes?.data?.data) {
        setCompanies(companiesRes.data.data.map((item) => ({ value: item.uuid, label: item.name })));
      }

      if (preferenRes?.data?.data) {
        setPreFerencesData(preferenRes.data.data);
      }

      const apiData = travellerRes?.data?.data;
      if (apiData) {
        const gData = apiData.data?.generalData || {};
        const emgContact = apiData.data?.contacts?.emergencyContact || {};
        const papers = apiData.data?.papers || { passports: [], visas: [], idCards: [] };
        const memberships = apiData.data?.memberships || { flight: [], hotel: [], rentalCar: [] };
        const payments = apiData.data?.creditCardInfo?.creditCards;
        const preferences = apiData.data?.preferences || {};
        const customFieldGroups = Array.isArray(preferenRes?.data?.data?.custom_fields) ? preferenRes.data.data.custom_fields : preferenRes?.data?.data?.custom_fields?.groups || [];
        const dateCustomFields = new Set(
          customFieldGroups
            .flatMap((g) => g.fields || [])
            .filter((f) => f.fieldType === "DATE")
            .map((f) => f.name),
        );

        const customFields = (apiData.data?.genericFieldValues || []).reduce((acc, item) => {
          if (item?.field?.name) {
            const isDateField = dateCustomFields.has(item.field.name) || /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(item.value);
            let val = item.value;
            if (val === "true") val = true;
            else if (val === "false") val = false;
            acc[item.field.name] = isDateField ? parseDateFromAPI(val) : val;
          }
          return acc;
        }, {});

        const mapPapers = (arr) =>
          (arr || []).map((p) => ({
            ...p,
            issueDate: parseDateFromAPI(p.issueDate),
            expiration: parseDateFromAPI(p.expiration),
          }));

        const nextFormData = {
          username: user?.email || "",
          firstname: apiData.firstname || "",
          middlename: apiData.middlename || "",
          name: apiData.name || "",
          companyUuid: apiData.data?.company?.uuid || "",
          birthdate: parseDateFromAPI(gData.birthdate),
          sex: gData.sex && gData.sex !== "UNDEF" ? gData.sex : gData.sex || "",
          mobilePhone: gData.mobilePhone || "",
          nationality: gData.nationality || "",
          language: gData.language || "en_GB",
          privatePhone: gData.privatePhone || "",
          businessPhone: gData.businessPhone || "",
          title: gData.greeting?.compoundTitle || "",
          email: apiData.email || gData.email || "",
          emgFirstname: emgContact.firstname || "",
          emgLastname: emgContact.lastname || "",
          emgPhone: emgContact.phone || "",
          emgEmail: emgContact.email || "",
          memberships,
          payments,
          papers: {
            passports: mapPapers(papers.passports),
            visas: mapPapers(papers.visas),
            idCards: mapPapers(papers.idCards),
          },
          preferences: {
            seatRequest: preferences.preferredSeat || "",
            mealRequest: preferences.preferredFood || "",
            smoker: !!preferences.smoker,
          },
          customFields,
        };
        setFormData(nextFormData);
        // Record this as the saved snapshot so we can diff against it on submit
        savedDataRef.current = nextFormData;
      }
    } catch (error) {
      console.error("Failed to load profile data:", error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (workspaceDetails?._id && page) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceDetails?._id, page, group]);

  const handleChange = (e, field) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    let errorMsg = null;
    if (field?.required && (!value || value.toString().trim() === "")) {
      errorMsg = `${field.label || "This field"} is required`;
    } else if (field?.type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errorMsg = "Please enter a valid e-mail address";
    } else if (field?.name === "birthdate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(value);
      if (selected > today) {
        errorMsg = "Date of Birth cannot be in the future";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const errorMessages = [];
    let firstErrorSection = null;

    const personalFields = getPersonalFields({ companies, countries: preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })), user });
    personalFields.forEach((field) => {
      const val = formData[field.name];
      if (field.required) {
        if (!val || val.toString().trim() === "") {
          newErrors[field.name] = `${field.label} is required`;
          errorMessages.push(`${field.label} is required`);
          if (!firstErrorSection) firstErrorSection = "general";
        }
      }
      if (field.type === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.name] = "Please enter a valid e-mail address";
        errorMessages.push("Please enter a valid e-mail address");
        if (!firstErrorSection) firstErrorSection = "general";
      }
      if (field.name === "birthdate" && val) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(val);
        if (selected > today) {
          newErrors[field.name] = "Date of Birth cannot be in the future";
          errorMessages.push("Date of Birth cannot be in the future");
          if (!firstErrorSection) firstErrorSection = "general";
        }
      }
    });

    if (formData.emgEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emgEmail)) {
      newErrors.emgEmail = "Please enter a valid emergency contact email address";
      errorMessages.push("Please enter a valid emergency contact email address");
      if (!firstErrorSection) firstErrorSection = "emergency";
    }

    (formData.papers?.passports || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`Passport #${num}: Nationality is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`Passport #${num}: Passport No. is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`Passport #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });

    (formData.papers?.visas || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`Visa #${num}: Country is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`Visa #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`Visa #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });

    (formData.papers?.idCards || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.country?.trim()) {
        errorMessages.push(`ID Card #${num}: Country is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.number?.trim()) {
        errorMessages.push(`ID Card #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
      if (!item.expiration?.trim()) {
        errorMessages.push(`ID Card #${num}: Expiry date is required`);
        if (!firstErrorSection) firstErrorSection = "passport";
      }
    });
    (formData.memberships?.flight || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Flight Program #${num}: Airline is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Flight Program #${num}: Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    (formData.memberships?.hotel || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Hotel Program #${num}: Hotel Chain is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Hotel Program #${num}: Customer Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    (formData.memberships?.rentalCar || []).forEach((item, index) => {
      if (item._operation === "remove") return;
      const num = index + 1;
      if (!item.alliance?.trim()) {
        errorMessages.push(`Car Program #${num}: Company is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
      if (!item.memberNumber?.trim()) {
        errorMessages.push(`Car Program #${num}: Customer Number is required`);
        if (!firstErrorSection) firstErrorSection = "frequentFlyer";
      }
    });

    setErrors(newErrors);

    return {
      isValid: errorMessages.length === 0,
      errorMessages,
      firstErrorSection,
    };
  };

  const buildPatch = (saved, current) => {
    const patch = {};

    // top-level scalar fields — field-by-field
    const scalarFields = ["username", "firstname", "middlename", "name"];
    scalarFields.forEach((key) => {
      if (current[key] !== saved[key]) patch[key] = current[key];
    });

    const dataBlock = {};

    // generalData — field-by-field, only changed keys go in
    const generalDataFieldMap = [
      { formKey: "birthdate", apiKey: "birthdate", transform: (v) => formatDateToAPI(v) },
      { formKey: "sex", apiKey: "sex" },
      { formKey: "mobilePhone", apiKey: "mobilePhone" },
      { formKey: "nationality", apiKey: "nationality" },
      { formKey: "language", apiKey: "language" },
      { formKey: "privatePhone", apiKey: "privatePhone" },
      { formKey: "businessPhone", apiKey: "businessPhone" },
      { formKey: "email", apiKey: "email" },
    ];

    const generalDataPatch = {};
    generalDataFieldMap.forEach(({ formKey, apiKey, transform }) => {
      if (current[formKey] !== saved[formKey]) {
        generalDataPatch[apiKey] = transform ? transform(current[formKey]) : current[formKey];
      }
    });
    // title maps to a nested object
    if (current.title !== saved.title) {
      generalDataPatch.greeting = { compoundTitle: current.title };
    }
    if (Object.keys(generalDataPatch).length > 0) {
      dataBlock.generalData = generalDataPatch;
    }

    // company — only if companyUuid changed
    if (current.companyUuid !== saved.companyUuid && current.companyUuid) {
      dataBlock.company = { uuid: current.companyUuid };
    }

    // emergency contact — field-by-field
    const emgFieldMap = [
      { formKey: "emgFirstname", apiKey: "firstname" },
      { formKey: "emgLastname", apiKey: "lastname" },
      { formKey: "emgPhone", apiKey: "phone" },
      { formKey: "emgEmail", apiKey: "email" },
    ];

    const emgPatch = {};
    emgFieldMap.forEach(({ formKey, apiKey }) => {
      if (current[formKey] !== saved[formKey]) {
        emgPatch[apiKey] = current[formKey];
      }
    });
    if (Object.keys(emgPatch).length > 0) {
      dataBlock.contacts = { emergencyContact: emgPatch };
    }

    // preferences — field-by-field
    const prefFieldMap = [
      { formKey: "seatRequest", apiKey: "preferredSeat", fallback: "" },
      { formKey: "mealRequest", apiKey: "preferredFood", fallback: "" },
      { formKey: "smoker", apiKey: "smoker", fallback: false },
    ];

    const prefPatch = {};
    prefFieldMap.forEach(({ formKey, apiKey, fallback }) => {
      if (current.preferences?.[formKey] !== saved.preferences?.[formKey]) {
        prefPatch[apiKey] = current.preferences?.[formKey] ?? fallback;
      }
    });
    if (Object.keys(prefPatch).length > 0) {
      dataBlock.preferences = prefPatch;
    }

    // papers — sub-type level diff: only changed sub-arrays go out
    const formatPapers = (arr) =>
      (arr || []).map((p) => ({
        ...p,
        issueDate: formatDateToAPI(p.issueDate),
        expiration: formatDateToAPI(p.expiration),
      }));

    const papersPatch = {};
    ["passports", "visas", "idCards"].forEach((type) => {
      if (JSON.stringify(current.papers?.[type]) !== JSON.stringify(saved.papers?.[type])) {
        papersPatch[type] = formatPapers(current.papers[type]);
      }
    });
    if (Object.keys(papersPatch).length > 0) {
      dataBlock.papers = papersPatch;
    }

    // memberships — sub-type level diff: only changed sub-arrays go out
    const membershipsPatch = {};
    ["flight", "hotel", "rentalCar"].forEach((type) => {
      if (JSON.stringify(current.memberships?.[type]) !== JSON.stringify(saved.memberships?.[type])) {
        membershipsPatch[type] = current.memberships[type];
      }
    });
    if (Object.keys(membershipsPatch).length > 0) {
      dataBlock.memberships = membershipsPatch;
    }

    // payments — whole block (no meaningful sub-structure to split)
    if (JSON.stringify(current.payments) !== JSON.stringify(saved.payments)) {
      dataBlock.creditCardInfo = {
        creditCards: current.payments,
        webCard: { "@type": "none" },
        hotelGuarantee: { "@type": "none" },
        carGuarantee: { "@type": "none" },
        railFormOfPayment: { "@type": "none" },
        additionalFormOfPayment: "UNDEFINED",
      };
    }

    // customFields — whole block diff
    if (JSON.stringify(current.customFields) !== JSON.stringify(saved.customFields)) {
      dataBlock.genericFieldValues = buildGenericFieldValues();
    }

    if (Object.keys(dataBlock).length > 0) {
      patch.data = { ...(group ? { group } : {}), ...dataBlock };
    }

    return patch;
  };

  const buildGenericFieldValues = () => {
    const groups = Array.isArray(preFerencesData?.custom_fields) ? preFerencesData.custom_fields : preFerencesData?.custom_fields?.groups || [];
    const fields = groups.flatMap((group) => group.fields || []);

    return fields
      .filter((field) => formData.customFields?.[field.name] !== undefined)
      .map((field) => {
        let val = formData.customFields[field.name];
        if (field.fieldType === "DATE" && val) {
          val = formatDateToAPI(val);
        }
        return {
          field: { uuid: field.uuid, name: field.name },
          value: val,
          elective: null,
        };
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation?.isValid) {
      if (validation?.firstErrorSection) {
        setActiveSection(validation?.firstErrorSection);
      }

      if (validation?.errorMessages.length === 1) {
        toast.error(validation?.errorMessages[0]);
      } else {
        const ToastErrorContent = () => (
          <div className="flex flex-col gap-1">
            <span className="font-bold text-red-700">Please correct the following errors:</span>
            <ul className="list-disc space-y-0.5 pl-4 text-xs">
              {validation?.errorMessages?.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        );
        toast.error(<ToastErrorContent />, { autoClose: 5000 });
      }
      return;
    }

    setIsSaving(true);

    const patch = buildPatch(savedDataRef.current, formData);

    if (Object.keys(patch).length === 0) {
      toast.info("No changes detected — nothing to save.");
      setIsSaving(false);
      return;
    }

    const payload = {
      ...(group ? { group } : {}),
      ...patch,
    };

    try {
      await apiRequest("put", `/portal/workspaces/${workspaceDetails?._id}/travel/profiles/travellers${queryParams}`, { body: payload });
      // Refresh and update the saved snapshot
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Form submit failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      key: field.name,
      label: field.label,
      name: field.name,
      value: formData[field.name],
      onChange: (e) => handleChange(e, field),
      required: field.required,
      className: field.col,
      placeholder: field.placeholder,
      error: errors[field.name],
      disabled: field.disabled,
    };

    return field.type === "select" ? (
      <TravelFormSelect
        {...commonProps}
        options={field.options || []}
        select_type={field?.select_type}
      />
    ) : (
      <TravelFormInput
        {...commonProps}
        type={field.type || "text"}
        maxDate={field.maxDate}
        minDate={field.minDate}
      />
    );
  };

  const renderActiveFormContent = () => {
    switch (activeSection) {
      case "general":
        return getPersonalFields({ companies, countries: preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })), user }).map(renderField);
      case "emergency":
        return getEmergencyFields().map(renderField);
      case "passport":
        return (
          <TravelPassportDocuments
            formData={formData}
            setFormData={setFormData}
            countries={preFerencesData?.countries?.map((item) => ({ value: item.code, label: item.name })) || []}
          />
        );
      case "frequentFlyer":
        return (
          <TravelPreferences
            formData={formData}
            setFormData={setFormData}
            preferencesData={preFerencesData}
          />
        );
      case "payments":
        return (
          <TravelPayments
            formData={formData}
            setFormData={setFormData}
          />
        );
      case "customFields":
        return (
          <TravelCustomFields
            formData={formData}
            setFormData={setFormData}
            customFieldsData={preFerencesData?.custom_fields}
          />
        );
      default:
        return null;
    }
  };

  const activeSectionData = FORM_SECTIONS.find((s) => s.id === activeSection);

  return (
    <div className={classNames("w-full px-4 py-6 sm:px-8 md:px-12 lg:px-16", !!onBack ? "mb-8" : "")}>
      <div className="mb-6 flex items-center justify-between md:mb-10">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#2D0B31] sm:text-3xl">Traveller Profile</h2>
          <p className="mt-1 text-xs text-gray-500 sm:text-sm">Manage your details, travel documents, and preferences.</p>
        </div>
      </div>

      {isFetching ? (
        <TravelFromSkeleton />
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="relative">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
            <div className="scrollbar-none sticky top-4 z-10 flex flex-row gap-2 overflow-x-auto pb-2 lg:top-6 lg:col-span-3 lg:flex-col lg:gap-3 lg:overflow-visible lg:pb-0 xl:col-span-3">
              {FORM_SECTIONS.map((section) => {
                const isActive = activeSection === section.id;

                return (
                  <div
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={classNames(
                      "flex shrink-0 cursor-pointer items-center justify-between gap-2 rounded-lg px-4 py-2.5 transition-all duration-200 lg:p-5",
                      isActive
                        ? "border border-transparent bg-[#2D0B31] text-white shadow-md lg:border-l-4 lg:border-gray-200 lg:border-l-[#2D0B31] lg:bg-gray-50 lg:text-[#2D0B31] lg:ring-1 lg:ring-black/5"
                        : "border border-gray-100 bg-white text-gray-600 shadow-sm hover:border-[#2D0B31]/30 hover:shadow-md",
                    )}>
                    <h3 className={classNames("whitespace-nowrap text-sm transition-colors lg:text-lg", isActive ? "font-extrabold text-white lg:text-[#2D0B31]" : "font-bold text-gray-600")}>{section.title}</h3>

                    <ChevronRight
                      size={16}
                      className="hidden lg:block"
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-6 lg:col-span-9 xl:col-span-9">
              <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm md:p-8">
                <div className="mb-8 flex items-center gap-3 border-b border-gray-50 pb-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2D0B31]/10 text-[#2D0B31]">
                    <UserPen size={20} />
                  </div>
                  <h3 className="text-2xl font-extrabold text-[#2D0B31]">{activeSectionData?.title}</h3>
                </div>

                <div className="animate-fade-in slide-in-from-bottom-2 grid grid-cols-1 gap-6 duration-300 md:grid-cols-12">{renderActiveFormContent()}</div>
              </div>

              <div className="flex items-center justify-end gap-3 rounded-lg border border-gray-100 bg-white px-6 py-4 shadow-sm">
                {!!onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-200 px-6 py-2.5 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50">
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex min-w-[140px] items-center justify-center gap-2 rounded-lg bg-[#2D0B31] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all duration-300 hover:bg-[#4a1352] hover:shadow-lg focus:ring-4 focus:ring-[#2D0B31]/30 disabled:cursor-not-allowed disabled:opacity-80">
                  {isSaving ? (
                    <>
                      <LoaderCircle
                        size={16}
                        className="animate-spin"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default TravellerProfileForm;
